/**
 * Admin · add a user straight from the Entra (Microsoft 365) directory.
 *
 * Searches the live directory (Graph, via /admin/directory/search), and imports
 * a chosen person into the users table as an ACTIVE user with the role the admin
 * picks — no self-service access request needed. Degrades with a clear message
 * when Graph isn't configured on the environment.
 */
import { useEffect, useState } from 'react';
import { Loader2, Search, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { RoleSchema, type DirectoryCandidate, type Role } from '@vari-crew/schemas';
import { useCreateUserFromDirectory, useDirectorySearch } from '@/api/access';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export function AddFromDirectoryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  // Debounce so a typeahead doesn't fire a Graph query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);
  // Fresh search box each time the dialog opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
    }
  }, [open]);

  const { data, isFetching, error } = useDirectorySearch(debounced, open);
  const create = useCreateUserFromDirectory();
  const candidates = data?.candidates ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={create.isPending}
      className="max-w-xl"
      title={
        <>
          <UserPlus className="w-4 h-4" /> Add user from directory
        </>
      }
    >
      <div className="p-4 space-y-3">
        <p className="text-xs text-ink-dim">
          Search your Microsoft Entra directory and add a teammate directly. They&apos;re activated
          immediately with the role you pick and get a sign-in email.
        </p>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-8"
          />
        </div>

        {data && !data.configured && (
          <div className="rounded-md border border-line bg-fill-soft p-3 text-xs text-ink-dim">
            Directory search isn&apos;t configured on this environment. Set{' '}
            <code className="text-[11px]">ENTRA_CLIENT_SECRET</code> and grant the app{' '}
            <code className="text-[11px]">User.Read.All</code> (admin-consented) to enable it.
          </div>
        )}
        {data?.configured && !data.available && (
          <div className="rounded-md border border-line bg-fill-soft p-3 text-xs text-ink-dim">
            The directory is unavailable right now — try again in a moment.
          </div>
        )}
        {error && (
          <div className="rounded-md border border-line p-3 text-xs text-status-blocked-fg">
            {(error as Error).message}
          </div>
        )}

        <div className="max-h-[50vh] overflow-auto">
          {isFetching && (
            <div className="py-6 text-center">
              <Loader2 className="w-4 h-4 animate-spin inline-block text-ink-faint" />
            </div>
          )}
          {!isFetching && data?.configured && data.available && candidates.length === 0 && (
            <div className="py-6 text-center text-xs text-ink-faint">
              {debounced ? 'No matching people.' : 'Start typing to search the directory.'}
            </div>
          )}
          <ul className="space-y-1.5">
            {candidates.map((c) => (
              <CandidateRow
                key={c.entra_oid}
                candidate={c}
                busy={create.isPending}
                onAdd={(role) =>
                  create.mutate(
                    { entra_oid: c.entra_oid, role },
                    {
                      onSuccess: (u) => toast.success(`${u.display_name} added as ${u.role}`),
                      onError: (e) => toast.error(e.message),
                    },
                  )
                }
              />
            ))}
          </ul>
        </div>

        <div className="flex justify-end pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={create.isPending}>
            Done
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function CandidateRow({
  candidate,
  busy,
  onAdd,
}: {
  candidate: DirectoryCandidate;
  busy: boolean;
  onAdd: (role: Role) => void;
}) {
  // Most directory imports are field crew; the admin can change it before adding.
  const [role, setRole] = useState<Role>('crew');
  const existing = candidate.existing_user_id != null;

  return (
    <li className="flex items-center gap-2 rounded-md border border-line p-2">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm truncate">{candidate.display_name}</div>
        <div className="text-xs text-ink-faint truncate">
          {candidate.email}
          {candidate.job_title ? ` · ${candidate.job_title}` : ''}
        </div>
      </div>
      {existing ? (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-line text-ink-faint capitalize whitespace-nowrap">
          Already a user{candidate.existing_status ? ` · ${candidate.existing_status}` : ''}
        </span>
      ) : (
        <>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={busy}
            className="px-2 py-1 text-xs border border-line rounded bg-bg capitalize"
          >
            {RoleSchema.options.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <Button size="sm" disabled={busy} onClick={() => onAdd(role)}>
            Add
          </Button>
        </>
      )}
    </li>
  );
}
